import * as React from "react";
import { MoneyDisplayHelper } from "../../../../../../../../react-core/Helpers/MoneyDisplayHelper";
import { Lightbox, LightboxType } from "../../../../../../../../react-core/Html/Components/Lightbox";
import { FormComponent } from "../../../../../../../../react-core/Html/FormComponent";
import { PartLoading } from "../../../../../../../../react-core/Html/PartLoading";
import { Guid } from "../../../../../../../../react-core/Utils/Guid";
import { JsonDateParser } from "../../../../../../../../react-core/Utils/JsonDateParser";
import { CurrencyCode } from "../../../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { CashEntryDetail } from "../../../../../../../../Wrap/Models/Domains/Transactions/Cash/CashEntryDetail";
import { CashLedgerTransaction } from "../../../../../../../../Wrap/Models/Domains/Transactions/Cash/CashLedgerTransaction";
import { Table } from "../../../../../../../Shared/Table/Table";
import { TransactionAction } from "../Actions/TransactionActions";
import { Contents } from "../Resource/Contents";

interface ICashDetailPopupViewProps {
    showBox: boolean;
    onChange: (val: boolean) => void;
    transId: number;
    orderId: number;
    chequeRef: string;
    cashAmount: number;
    productName: string;
    clAccountId: string;
}

interface ICashDetailPopupViewState {
    cashLedgerTransactionList: Array<CashLedgerTransaction>;
    autoDisInvestChargeList: Array<CashLedgerTransaction>;
    cashEntryDetailList: Array<CashEntryDetail>;
    isCashLedgerLoading: boolean;
    isAutoDisInvestLoading: boolean;
    isCashEntryLoading: boolean;
}

export class CashDetailPopupView extends FormComponent<ICashDetailPopupViewProps, ICashDetailPopupViewState> {
    constructor(props: ICashDetailPopupViewProps) {
        super(props);
        this.state = {
            cashLedgerTransactionList: [],
            autoDisInvestChargeList: [],
            cashEntryDetailList: [],
            isCashLedgerLoading: true,
            isAutoDisInvestLoading: true,
            isCashEntryLoading: true,
        };
    }

    componentDidMount() {
        this.getData();
    }

    private getData() {
        const { orderId, transId } = this.props;
        TransactionAction.getCashLedgerTransactionWithBatchId(transId, (data: Array<CashLedgerTransaction>) => {
            this.setState({
                isCashLedgerLoading: false,
                cashLedgerTransactionList: data,
            });

            let cashId = orderId;
            if (orderId > 0 && data[0].Reference) {
                const reg = new RegExp("Cash", "g");
                const referenceMatch = data[0].ClAccountId.replace(reg, "");
                if (!isNaN(Number(referenceMatch)) && Number(referenceMatch) != orderId) {
                    cashId = Number(referenceMatch);
                }
            }
            TransactionAction.getCashEntryDetail(cashId, (data: Array<CashEntryDetail>) => {
                this.setState({
                    isCashEntryLoading: false,
                    cashEntryDetailList: data,
                });
            });
        });

        TransactionAction.getAutoDisInvestTranWithBatchId(transId, (callBack: Array<CashLedgerTransaction>) => {
            this.setState({
                isAutoDisInvestLoading: false,
                autoDisInvestChargeList: callBack,
            });
        });
    }

    private renderModal(): JSX.Element {
        return (
            <Lightbox
                show={this.props.showBox}
                title="Cash transaction detail"
                contentAfterTitle={null}
                onClose={() => this.props.onChange(false)}
                type={LightboxType.Modal}
                unclosable={false}
                noOverlay={false}
                noAutofocus={false}
                cssWrapper={"is-default-lightbox is-wide"}
            >
                {this.getModalBody()}
            </Lightbox>
        );
    }

    private getModalBody(): JSX.Element {
        const { orderId, clAccountId, productName, transId } = this.props;
        const { isCashLedgerLoading, isAutoDisInvestLoading, isCashEntryLoading } = this.state;

        if (isCashLedgerLoading || isAutoDisInvestLoading || isCashEntryLoading) {
            return <PartLoading isLoading={true} />;
        }
        return (
            <>
                <h2 className="mb-40">{`Detail for Transaction #${transId} for Account ${clAccountId} - ${productName}`}</h2>
                <div className="content">
                    {this.renderTransactionDetailTable()}
                    {this.renderAutoDisInvestChargeTable()}
                    {this.renderCurrentCashExpectationDetailTable()}
                    {this.renderCashExpectationEditHistoryTable()}
                    {this.renderStandingCashExpectationTable()}
                </div>
            </>
        );
    }

    private renderTransactionDetailTable() {
        const { cashAmount } = this.props;
        const { cashLedgerTransactionList } = this.state;
        if (cashLedgerTransactionList && cashLedgerTransactionList.length == 0) {
            return null;
        }
        const batchId = cashLedgerTransactionList[0].BatchId;
        let displayModifier = -1;
        if (cashLedgerTransactionList[0].Amount / cashAmount > 0) {
            displayModifier = 1;
        }
        return (
            <>
                <h3>Transaction Detail</h3>
                <Table
                    data={cashLedgerTransactionList}
                    col={[
                        {
                            title: "ID",
                            key: "TransID",
                            render: (data, row: CashLedgerTransaction, index) => {
                                return row.TransId.toString();
                            },
                        },
                        {
                            title: "Account No",
                            key: "ClAccountId",
                        },
                        {
                            title: "Ledger date",
                            key: "LedgerDate",
                            render: (data, row: CashLedgerTransaction, index) => {
                                return JsonDateParser.getFormatDateString(
                                    JsonDateParser.toDate(row.LedgerDate),
                                    "dd-MM-yyyy",
                                    true,
                                    true
                                );
                            },
                        },
                        {
                            title: "Date created",
                            key: "DateCreated",
                            render: (data, row: CashLedgerTransaction, index) => {
                                return (
                                    JsonDateParser.getFormatDateString(
                                        JsonDateParser.toDate(row.DateCreated),
                                        "dd-MM-yyyy hh:mm",
                                        true,
                                        true
                                    ) +
                                    " " +
                                    this.getHourType(JsonDateParser.toDate(row.DateCreated))
                                );
                            },
                        },
                        {
                            title: "Movement type",
                            key: "TranType",
                        },
                        {
                            title: "Narrative",
                            key: "Narrative",
                        },
                        {
                            title: "MCAInitiator",
                            key: "McaInitiatorName",
                        },
                        {
                            title: "MCAAuthoriser",
                            key: "McaAuthoriserName",
                        },
                        {
                            title: "Amount",
                            key: "Amount",
                            thCssWrapper: "cell-right",
                            tdCssWrapper: "cell-right",
                            render: (data, row: CashLedgerTransaction, index) => {
                                return MoneyDisplayHelper.FromatMoneyDisplay(data * displayModifier, null, false, 2);
                            },
                        },
                        batchId > 0
                            ? {
                                  title: "Unique ID",
                                  key: "BatchId",
                              }
                            : {},
                    ]}
                />
            </>
        );
    }

    private renderAutoDisInvestChargeTable() {
        const { autoDisInvestChargeList, cashLedgerTransactionList } = this.state;
        if (autoDisInvestChargeList && autoDisInvestChargeList.length == 0) {
            return null;
        }
        const { cashAmount } = this.props;
        let displayModifier = -1;
        if (cashLedgerTransactionList[0].Amount / cashAmount > 0) {
            displayModifier = 1;
        }

        return (
            <>
                <h3>Charge Details</h3>
                <Table
                    data={autoDisInvestChargeList}
                    col={[
                        {
                            title: "Charge type",
                            key: "TranType",
                        },
                        {
                            title: "Charge date",
                            key: "DateCreated",
                            render: (data, row: CashLedgerTransaction, index) => {
                                return JsonDateParser.getFormatDateString(
                                    JsonDateParser.toDate(row.DateCreated),
                                    "dd-MM-yyyy hh:mm",
                                    true,
                                    true
                                );
                            },
                        },
                        {
                            title: "Charge amount",
                            key: "Amount",
                            thCssWrapper: "cell-right",
                            tdCssWrapper: "cell-right",
                            render: (data, row: CashLedgerTransaction, index) => {
                                return MoneyDisplayHelper.FromatMoneyDisplay(
                                    row.Amount * displayModifier,
                                    null,
                                    false,
                                    2
                                );
                            },
                        },
                    ]}
                />
            </>
        );
    }

    private renderCurrentCashExpectationDetailTable() {
        const { orderId } = this.props;
        const { chequeRef } = this.props;
        const { cashEntryDetailList } = this.state;
        if (cashEntryDetailList && cashEntryDetailList.length == 0) {
            return null;
        }
        const cashEntryDetailTop = cashEntryDetailList.concat().splice(0, 1);
        const isShowBankAccount =
            cashEntryDetailTop[0].Method.toUpperCase() == "DIRECT CREDIT" ||
            cashEntryDetailTop[0].Method.toUpperCase() == "DIRECT DEBIT" ||
            cashEntryDetailTop[0].Method.toUpperCase() == "CHEQUE";
        const isShowMatched = isShowBankAccount || cashEntryDetailTop[0].Method.toUpperCase() == "CHEQUE";
        return (
            <>
                <h3>{`This transaction was originated from Cash Expectation #${orderId}`}</h3>
                <h4>Current Cash Expectation Detail</h4>
                <Table
                    data={cashEntryDetailTop}
                    col={[
                        {
                            title: "Date created",
                            key: "DateCreated",
                            render: (data, row: CashEntryDetail, index) => {
                                return (
                                    JsonDateParser.getFormatDateString(
                                        JsonDateParser.toDate(row.DateCreated),
                                        "dd-MM-yyyy hh:mm",
                                        true,
                                        true
                                    ) +
                                    " " +
                                    this.getHourType(JsonDateParser.toDate(row.DateCreated))
                                );
                            },
                        },
                        {
                            title: "Entered by",
                            key: "FirstName",
                            render: (data, row: CashEntryDetail, index) => {
                                return row.FirstName + " " + row.SurName;
                            },
                        },
                        isShowBankAccount
                            ? {
                                  title: "Bank account",
                                  key: "DateEdited",
                                  render: (data, row: CashEntryDetail, index) => {
                                      const sortCode = row.SortCode == null ? "" : row.SortCode;
                                      const accountNum = row.AccountNumber == null ? "" : row.AccountNumber;
                                      return sortCode + "-" + accountNum;
                                  },
                              }
                            : {},
                        {
                            title: "Amount",
                            key: "Amount",
                            thCssWrapper: "cell-right",
                            tdCssWrapper: "cell-right",
                            render: (data, row: CashEntryDetail, index) => {
                                return this.getMoneyFormat(row.Amount);
                            },
                        },
                        {
                            title: "Transaction type",
                            key: "Type",
                        },
                        {
                            title: "Method",
                            key: "Method",
                        },
                        {
                            title: "Implemt. Fee",
                            key: "ImplementationFee",
                            render: (data, row: CashEntryDetail, index) => {
                                return row.ImplementationFee.toFixed(4) + "%";
                            },
                        },
                        {
                            title: "Schedule type",
                            key: "InstructionType",
                        },
                        {
                            title: "Cheque reference",
                            key: "chequeRef",
                            render: (data, row: CashEntryDetail, index) => {
                                return chequeRef;
                            },
                        },
                        isShowMatched
                            ? {
                                  title: "User matched",
                                  key: "OrderSettLocation",
                                  render: (data: string, row: CashEntryDetail, index) => {
                                      if (isShowBankAccount) {
                                          return row.BosUserName;
                                      } else if (cashEntryDetailTop[0].Method.toUpperCase() == "CHEQUE") {
                                          return row.KesUserName;
                                      } else {
                                          return null;
                                      }
                                  },
                              }
                            : {},
                        isShowMatched
                            ? {
                                  title: "Matched date",
                                  key: "OrderSettLocation",
                                  render: (data: string, row: CashEntryDetail, index) => {
                                      if (isShowBankAccount) {
                                          if (row?.BosMatchDateTime + "" != Contents.InvalidateDate) {
                                              return (
                                                  JsonDateParser.getFormatDateString(
                                                      JsonDateParser.toDate(row.BosMatchDateTime),
                                                      "dd-MM-yyyy hh:mm",
                                                      true,
                                                      true
                                                  ) +
                                                  " " +
                                                  this.getHourType(JsonDateParser.toDate(row.DateCreated))
                                              );
                                          } else {
                                              return "null";
                                          }
                                      } else if (cashEntryDetailTop[0].Method.toUpperCase() == "CHEQUE") {
                                          return row.KesUserName;
                                      } else {
                                          return null;
                                      }
                                  },
                              }
                            : {},
                    ]}
                />
            </>
        );
    }

    private renderCashExpectationEditHistoryTable() {
        const { cashEntryDetailList } = this.state;
        const cashEntryDetailMid = cashEntryDetailList.concat().slice(1);
        if (cashEntryDetailMid && cashEntryDetailMid.length == 0) {
            return null;
        }
        let pbShowBankAcc = false;
        cashEntryDetailList.map((item: CashEntryDetail, index) => {
            if (item.Method.toUpperCase() == "DIRECT CREDIT" || item.Method.toUpperCase() == "DIRECT DEBIT") {
                pbShowBankAcc = true;
                return false;
            }
        });
        return (
            <>
                <h3>Cash Expectation Edit History Detail</h3>
                <table className="data-table">
                    <thead>
                        <tr className="sub-head">
                            <th>EditID</th>
                            <th>Date Edited</th>
                            <th>Entered By</th>
                            {pbShowBankAcc ? (
                                <th>
                                    Bank
                                    <br />
                                    Account
                                </th>
                            ) : null}
                            <th>Amount</th>
                            <th>
                                Transaction
                                <br />
                                Type
                            </th>
                            <th>Method</th>
                            <th>
                                Implemt.
                                <br /> Fee
                            </th>
                            <th>
                                Schedule
                                <br />
                                Type
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {cashEntryDetailMid.map((item: CashEntryDetail, index) => {
                            if (item.InstructionType.toUpperCase() == "STANDING") {
                                return false;
                            }
                            return (
                                <tr key={index + Guid.getNext()}>
                                    <td>{item.EditId}</td>
                                    <td>
                                        {JsonDateParser.getFormatDateString(
                                            JsonDateParser.toDate(item.DateEdited),
                                            "dd-MM-yyyy hh:mm",
                                            true,
                                            true
                                        ) +
                                            " " +
                                            this.getHourType(JsonDateParser.toDate(item.DateEdited))}
                                    </td>
                                    <td>{item.FirstName + " " + item.SurName}</td>
                                    {pbShowBankAcc ? <td>{item.SortCode + "-" + item.AccountNumber}</td> : null}
                                    <td>{this.getMoneyFormat(item.Amount)}</td>
                                    <td>{item.Type}</td>
                                    <td>{item.Method}</td>
                                    <td>{item.ImplementationFee.toFixed(4) + "%"}</td>
                                    <td>{item.InstructionType}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </>
        );
    }

    private renderStandingCashExpectationTable() {
        const { cashEntryDetailList } = this.state;
        const cashEntryDetailLast = cashEntryDetailList.concat().slice(-1);
        const standingCash = cashEntryDetailLast[0];
        if (cashEntryDetailLast && cashEntryDetailLast.length == 0) {
            return null;
        }
        const pbShowBankAcc =
            standingCash.Method.toUpperCase() == "DIRECT CREDIT" || standingCash.Method.toUpperCase() == "DIRECT DEBIT";
        if (standingCash.InstructionType.toUpperCase() == "STANDING") {
            return (
                <>
                    <h3>Current Standing Cash Expectation Detail</h3>
                    <table className="data-table">
                        <thead>
                            <tr className="sub-head">
                                <th>Date Created</th>
                                <th>Entered By</th>
                                {pbShowBankAcc ? (
                                    <td>
                                        Bank
                                        <br />
                                        Account
                                    </td>
                                ) : null}
                                <th>Amount</th>
                                <th>
                                    Transaction
                                    <br />
                                    Type
                                </th>
                                <th>Method</th>
                                <th>
                                    Implemt.
                                    <br /> Fee
                                </th>
                                <th>
                                    Schedule
                                    <br />
                                    Type
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>
                                    {JsonDateParser.getFormatDateString(
                                        JsonDateParser.toDate(standingCash.DateEdited),
                                        "dd-MM-yyyy hh:mm",
                                        true,
                                        true
                                    ) +
                                        " " +
                                        this.getHourType(JsonDateParser.toDate(standingCash.DateEdited))}
                                </td>
                                <td>{standingCash.FirstName + " " + standingCash.SurName}</td>
                                {pbShowBankAcc ? (
                                    <td>{standingCash.SortCode + "-" + standingCash.AccountNumber}</td>
                                ) : null}
                                <td>{this.getMoneyFormat(standingCash.Amount)}</td>
                                <td>{standingCash.Type}</td>
                                <td>{standingCash.Method}</td>
                                <td>{standingCash.ImplementationFee.toFixed(4) + "%"}</td>
                                <td>{standingCash.InstructionType}</td>
                            </tr>
                        </tbody>
                    </table>
                </>
            );
        } else {
            return null;
        }
    }

    private getMoneyFormat(val: number): string {
        const formatMoney = MoneyDisplayHelper.FromatMoneyDisplay(val, CurrencyCode.GBP, true, 2);
        return formatMoney;
    }

    private getHourType(date: Date) {
        if (!date) {return "";}

        let h = date.getHours();
        let str;
        if (h > 12) {
            h -= 12;
            date.setHours(h);
            str = " pm";
        } else {
            str = " am";
        }
        return str;
    }

    public render(): JSX.Element {
        return <>{this.props.showBox ? this.renderModal() : null}</>;
    }
}
